/**
 * Extended feature usage utilities for specific scenarios
 * This provides more context-aware error messages for different parts of the application
 */

import { getFeatureUsageMessage, showFeatureUsageToast, type FeatureUsageReason } from './featureUsageMessages';

export interface FeatureContext {
    component?: string;
    action?: string;
    metadata?: Record<string, any>;
}

/**
 * Enhanced feature usage message getter with context awareness
 */
export function getContextualFeatureUsageMessage(
    reason: FeatureUsageReason,
    featureKey: string,
    context?: FeatureContext & {
        remainingInBundle?: number;
        consumedInPeriod?: number;
        limitForPeriod?: number;
        chargedCredits?: number;
    }
) {
    const baseMessage = getFeatureUsageMessage(reason, featureKey, context);
    
    // Add contextual information based on the component and action
    if (context?.component && context?.action) {
        switch (context.component) {
            case 'ai-chat':
                if (reason === 'denied_limit_exceeded_no_overage_option') {
                    baseMessage.description = `You've reached your monthly AI chat limit. You can't send more messages until your plan resets or you upgrade to a higher plan.`;
                } else if (reason === 'denied_insufficient_credits_for_overage') {
                    baseMessage.description = `You don't have enough credits to continue chatting with AI. Purchase more credits to keep the conversation going.`;
                }
                break;
                
            case 'reports':
                if (reason === 'denied_limit_exceeded_no_overage_option') {
                    baseMessage.description = `You've reached your monthly report limit. You can't generate more reports until your plan resets or you upgrade.`;
                } else if (reason === 'denied_insufficient_credits_for_overage') {
                    baseMessage.description = `You don't have enough credits to generate this report. Purchase more credits to continue analyzing your websites.`;
                }
                break;
                
            case 'competitor-analysis':
                if (reason === 'denied_limit_exceeded_no_overage_option') {
                    baseMessage.description = `You've reached your monthly competitor analysis limit. Upgrade your plan to analyze more competitors.`;
                }
                break;
                
            case 'monitoring':
                if (reason === 'denied_limit_exceeded_no_overage_option') {
                    baseMessage.description = `You've reached your monthly monitoring limit. Upgrade your plan to monitor more metrics.`;
                }
                break;
        }
    }
    
    return baseMessage;
}

/**
 * Feature-specific error handlers
 */
export const FeatureErrorHandlers = {
    aiChat: (reason: FeatureUsageReason, modelType: 'vertex' | 'bedrock' = 'bedrock') => {
        void modelType;
        const featureKey = 'agent_compute_units';
        return getContextualFeatureUsageMessage(reason, featureKey, {
            component: 'ai-chat',
            action: 'send-message'
        });
    },
    
    reports: (reason: FeatureUsageReason) => {
        return getContextualFeatureUsageMessage(reason, 'reports', {
            component: 'reports',
            action: 'generate-report'
        });
    },
    
    competitorAnalysis: (reason: FeatureUsageReason) => {
        return getContextualFeatureUsageMessage(reason, 'competitor_reports', {
            component: 'competitor-analysis',
            action: 'analyze-competitor'
        });
    },
    
    monitoring: (reason: FeatureUsageReason) => {
        return getContextualFeatureUsageMessage(reason, 'monitoring_metrics', {
            component: 'monitoring',
            action: 'track-metrics'
        });
    }
};

/**
 * Pre-configured toast handlers for common scenarios
 */
export const FeatureToastHandlers = {
    aiChat: (reason: FeatureUsageReason, modelType: 'vertex' | 'bedrock' = 'bedrock', toastFn: any) => {
        const message = FeatureErrorHandlers.aiChat(reason, modelType);
        if (reason.startsWith('denied_')) {
            toastFn.error(message.title, { description: message.description });
        } else if (reason.startsWith('allowed_overage')) {
            toastFn.warning(message.title, { description: message.description });
        } else {
            toastFn.info(message.title, { description: message.description });
        }
        return message;
    },
    
    reports: (reason: FeatureUsageReason, toastFn: any) => {
        const message = FeatureErrorHandlers.reports(reason);
        if (reason.startsWith('denied_')) {
            toastFn.error(message.title, { description: message.description });
        } else if (reason.startsWith('allowed_overage')) {
            toastFn.warning(message.title, { description: message.description });
        } else {
            toastFn.info(message.title, { description: message.description });
        }
        return message;
    },
    
    competitorAnalysis: (reason: FeatureUsageReason, toastFn: any) => {
        const message = FeatureErrorHandlers.competitorAnalysis(reason);
        if (reason.startsWith('denied_')) {
            toastFn.error(message.title, { description: message.description });
        } else if (reason.startsWith('allowed_overage')) {
            toastFn.warning(message.title, { description: message.description });
        } else {
            toastFn.info(message.title, { description: message.description });
        }
        return message;
    },
    
    monitoring: (reason: FeatureUsageReason, toastFn: any) => {
        const message = FeatureErrorHandlers.monitoring(reason);
        if (reason.startsWith('denied_')) {
            toastFn.error(message.title, { description: message.description });
        } else if (reason.startsWith('allowed_overage')) {
            toastFn.warning(message.title, { description: message.description });
        } else {
            toastFn.info(message.title, { description: message.description });
        }
        return message;
    }
};
